import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import {
  USER_STORAGE_QUOTA_BYTES,
  USER_STORAGE_QUOTA_LABEL,
  USER_STORAGE_QUOTA_SCOPE_LABEL
} from '../constants/userStorageQuota';
import { isQuotaScopedStoragePath } from '../utils/quotaScopedStoragePaths';

export class StorageQuotaExceededError extends Error {
  readonly code = 'storage-quota-exceeded';

  constructor(message = `스토리지 용량 한도(${USER_STORAGE_QUOTA_LABEL})를 초과할 수 없습니다.`) {
    super(message);
    this.name = 'StorageQuotaExceededError';
  }
}

type UsageCache = {
  userId: string;
  usedBytes: number;
};

let usageCache: UsageCache | null = null;

export const userStorageQuotaService = {
  quotaBytes: USER_STORAGE_QUOTA_BYTES,
  quotaLabel: USER_STORAGE_QUOTA_LABEL,
  quotaScopeLabel: USER_STORAGE_QUOTA_SCOPE_LABEL,

  /** 업로드·삭제 등 Storage 변경 후 호출 — 다음 조회 시 서버에서 다시 읽음 */
  invalidateUsageCache() {
    usageCache = null;
  },

  getCachedUsageBytes(userId: string): number | null {
    if (usageCache?.userId === userId) {
      return usageCache.usedBytes;
    }
    return null;
  },

  formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024) {
      const mb = bytes / (1024 * 1024);
      return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)}MB`;
    }
    if (bytes >= 1024) {
      return `${Math.round(bytes / 1024)}KB`;
    }
    return `${bytes}B`;
  },

  async fetchUsageBytes(userId: string, options?: { force?: boolean }): Promise<number> {
    if (!options?.force && usageCache?.userId === userId) {
      return usageCache.usedBytes;
    }

    const callable = httpsCallable<void, { usedBytes: number; quotaBytes: number }>(
      functions,
      'getUserStorageUsage'
    );
    const result = await callable();
    const usedBytes = result.data?.usedBytes ?? 0;
    usageCache = { userId, usedBytes };
    return usedBytes;
  },

  async assertCanUpload(userId: string, additionalBytes: number, replacePath?: string): Promise<void> {
    if (replacePath != null && !isQuotaScopedStoragePath(replacePath)) {
      return;
    }

    const usedBytes = await this.fetchUsageBytes(userId);
    let previousSize = 0;

    if (replacePath) {
      try {
        const { getMetadata, ref: storageRef } = await import('firebase/storage');
        const { storage } = await import('../firebase/config');
        const meta = await getMetadata(storageRef(storage, replacePath));
        previousSize = meta.size || 0;
      } catch {
        previousSize = 0;
      }
    }

    if (usedBytes - previousSize + additionalBytes > USER_STORAGE_QUOTA_BYTES) {
      throw new StorageQuotaExceededError();
    }
  }
};
